import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../stores/auth';
import api from '../utils/api';
import { useTracking } from '../hooks/useTracking';

interface LoginForm {
  email: string;
  password: string;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const { register, handleSubmit: formHandleSubmit, formState: { errors } } = useForm<LoginForm>();
  const { setAuth, isAuthenticated, isInitialized } = useAuth();
  const queryClient = useQueryClient();
  
  const { trackLogin, trackPageView, trackEvent } = useTracking();

  const loginMutation = useMutation(
    async (data: LoginForm) => {
      console.log('[Login] Starting API call to /auth/login with:', { email: data.email, passwordLength: data.password.length });
      const response = await api.post('/auth/login', data);
      console.log('[Login] API response:', response.status, response.data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log('[Login] MUTATION onSuccess triggered', data);
        const authToken = data?.token || data?.user?.token;
        console.log('[Login] authToken extracted:', authToken ? 'present' : 'MISSING');
        setAuth(data.user, authToken);

        // Refresh nonce dopo login — wp_set_auth_cookie attiva sessione WP ma nonce pre-login è stale
        if (data?.nonce && typeof window !== 'undefined') {
          const api = (window as any).BOAT_TRADER_API;
          if (api) api.nonce = data.nonce;
        }

        const authDependentKeys = [
          ['boats'], ['motors'], ['dealers'], ['trades'],
          ['my-boats'], ['my-motors'], ['reservations'],
          ['categories'], ['filterOptions'], ['priceRange'],
          ['motorFilterOptions'], ['motorPriceRange'],
          ['cities'], ['provinces'], ['regions'],
        ];
        
        // Clear ALL cached queries so grid doesn't show stale data on mount
        queryClient.clear();
        
        authDependentKeys.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
        });
        
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            if (typeof key === 'string') {
              return ['boats', 'motors', 'dealers', 'trades', 'my-', 'reservations', 'categories', 'filter', 'price', 'city', 'province', 'region'].some(prefix => key.startsWith(prefix));
            }
            if (Array.isArray(key) && key[0]) {
              const first = key[0];
              return ['boats', 'motors', 'dealers', 'trades', 'my-', 'reservations', 'categories', 'filter', 'price', 'city', 'province', 'region'].some(prefix => first.startsWith(prefix));
            }
            return false;
          }
        });
        
        toast.success('Login effettuato con successo!');
        // navigate via useEffect when isAuthenticated flips
        // trackLogin removed — already handled by setAuthCallback in auth.tsx
      },
      onError: (error: any) => {
        console.error('[Login] MUTATION onError triggered', error);
        console.error('[Login] Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          code: error.response?.data?.code,
          message: error.response?.data?.message,
          errorField: error.response?.data?.error,
        });
        const errData = error.response?.data;
        const errMsg = errData?.message || errData?.error || errData?.code || '';
        if (errMsg.toLowerCase().includes('password') || errMsg.toLowerCase().includes('credenziali') || errMsg.toLowerCase().includes('invalid_credentials')) {
          toast.error('Email o password non corretti');
        } else if (errMsg.toLowerCase().includes('utente') || errMsg.toLowerCase().includes('attiv')) {
          toast.error('Account non ancora attivo. Controlla la email.');
        } else {
          toast.error('Errore durante il login. Riprova.');
        }
        trackEvent('user_login_failed', 'system', 'login', 'failed').catch(console.error);
      },
    }
  );

  const onSubmit = useCallback((data: LoginForm) => {
    console.log('[Login] onSubmit fired with:', { email: data.email, passwordLength: data.password.length });
    console.log('[Login] loginMutation.isLoading:', loginMutation.isLoading);
    loginMutation.mutate(data);
    console.log('[Login] mutate() called');
  }, [loginMutation.mutate]);

  const isMounted = useRef(true);
  useEffect(() => {
    document.body.style.background = '#050507';
    return () => { 
      isMounted.current = false;
      document.body.style.background = '';
    };
  }, []);

  useEffect(() => {
    trackPageView('/login', window.location.href, 'login').catch(console.error);
  }, []);

  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      const t = setTimeout(() => navigate('/boats', { replace: true }), 0);
      return () => clearTimeout(t);
    }
  }, [isInitialized, isAuthenticated, navigate]);

  useEffect(() => {
    const dealerEmail = searchParams.get('dealer_email');
    if (dealerEmail) {
      const timer = setTimeout(() => {
        if (isMounted.current && isInitialized && !isAuthenticated) {
          formHandleSubmit(onSubmit)();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchParams, formHandleSubmit, onSubmit, isInitialized, isAuthenticated]);

  const styles = {
    bg: {
      position: 'fixed' as const,
      inset: 0,
      background: `
        radial-gradient(ellipse 100% 80% at 50% 120%, rgba(34, 211, 238, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse 80% 60% at 80% 20%, rgba(249, 115, 22, 0.05) 0%, transparent 40%),
        linear-gradient(180deg, #050507 0%, #0a0a0f 50%, #050507 100%)
      `,
      zIndex: -2,
    },
    container: {
      position: 'relative',
      zIndex: 10,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    card: {
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '20px',
      padding: '32px',
      backdropFilter: 'blur(20px)',
    },
    input: {
      width: '100%',
      background: '#0a0a0f',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px',
      padding: '14px 16px',
      fontSize: '15px',
      color: '#ffffff',
      outline: 'none',
      transition: 'all 0.2s',
    },
    btn: {
      width: '100%',
      background: 'linear-gradient(135deg, #f97316, #ea580c)',
      border: 'none',
      borderRadius: '12px',
      padding: '16px',
      fontSize: '15px',
      fontWeight: 600,
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 20px rgba(249, 115, 22, 0.3)',
    },
  };

  return (
    <>
      <style>{`
        @keyframes gridPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .login-grid {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background-image: 
            linear-gradient(rgba(34, 211, 238, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.4) 1px, transparent 1px) !important;
          background-size: 60px 60px !important;
          background-repeat: repeat !important;
          z-index: 0 !important;
          animation: gridPulse 3s ease-in-out infinite;
        }
      `}</style>
      <div style={styles.bg}></div>
      <div className="login-grid"></div>
      
      <div style={styles.container}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img 
              src={((window as any).BOAT_TRADER_API?.plugin_url || '/wp-content/plugins/boat-trader-v3/assets/images/') + 'sealevel.webp'} 
              alt="Sea Level" 
              style={{ height: '56px', marginBottom: '16px', filter: 'brightness(0) invert(1)' }}
            />
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>Boat Trader B2B</div>
          </div>
          
          {/* Card */}
          <div style={styles.card}>
            <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', textAlign: 'center', color: '#ffffff' }}>Benvenuto</h1>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: '28px' }}>Accedi al tuo account dealer</p>
            
            <form onSubmit={formHandleSubmit(onSubmit)}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'rgba(255,255,255,0.7)' }}>Email</label>
                <input
                  type="email"
                  {...register('email', { required: 'Email obbligatoria' })}
                  placeholder="dealer@azienda.it"
                  autoComplete="email"
                  inputMode="email"
                  style={styles.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#22d3ee';
                    e.target.style.boxShadow = '0 0 0 3px rgba(34, 211, 238, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors.email && (
                  <p style={{ color: '#f87171', fontSize: '13px', marginTop: '6px' }}>{errors.email.message}</p>
                )}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'rgba(255,255,255,0.7)' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', { required: 'Password obbligatoria' })}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{ ...styles.input, paddingRight: '48px' }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#22d3ee';
                      e.target.style.boxShadow = '0 0 0 3px rgba(34, 211, 238, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p style={{ color: '#f87171', fontSize: '13px', marginTop: '6px' }}>{errors.password.message}</p>
                )}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ width: '18px', height: '18px', accentColor: '#22d3ee' }} />
                  <span>Ricordami</span>
                </label>
                <a href="#" style={{ fontSize: '13px', color: '#22d3ee', textDecoration: 'none' }}>Password dimenticata?</a>
              </div>
              
              <button
                type="submit"
                disabled={loginMutation.isLoading}
                style={styles.btn}
              >
                {loginMutation.isLoading ? 'Accesso in corso...' : 'Accedi'}
              </button>
            </form>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '24px 0' }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>oppure</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }}></div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                <span style={{ fontWeight: 600, color: '#ea580c' }}>G</span> Google
              </button>
              <button style={{ flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                <span style={{ fontWeight: 600, color: '#0ea5e9' }}>in</span> LinkedIn
              </button>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
              Non hai un account?{' '}
              <Link to="/register" style={{ color: '#22d3ee', textDecoration: 'none', fontWeight: 500 }}>Registrati come dealer</Link>
            </div>
            
            {/* Info B2B */}
            <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(34, 211, 238, 0.05)', border: '1px solid rgba(34, 211, 238, 0.2)', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#22d3ee', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Accesso riservato dealer</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                Sea Level è una piattaforma B2B per professionisti del settore nautico.
              </div>
              <ul style={{ marginTop: '8px', paddingLeft: '16px' }}>
                <li style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>✓ Cantiere nautico</li>
                <li style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>✓ Concessionario</li>
                <li style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>✓ Rivenditore</li>
              </ul>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Sea Level v3.0 • © 2026</div>
        </div>
      </div>
    </>
  );
}